import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch {
      // Backend always returns 200 — a network-level error is the only reason we get here.
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Link to="/auth" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6" data-testid="back-to-login">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
        <h1 className="text-3xl font-bold text-[#001f5c] mb-2">Reset your password</h1>
        <p className="text-sm text-slate-600 mb-8">Enter the email address associated with your SpendLens account and we'll send you a link to reset your password.</p>

        {sent ? (
          <div className="p-5 rounded-lg bg-blue-50 border border-blue-200" data-testid="reset-sent-block">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-blue-900">Check your inbox</p>
                <p className="text-sm text-blue-800 mt-1">If an account exists for <span className="font-medium">{email}</span>, a password-reset link is on its way. The link expires in 30 minutes.</p>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="forgot-password-form">
            <div className="space-y-1.5">
              <Label htmlFor="fp-email" className="text-[11px] font-semibold tracking-[0.14em] uppercase text-slate-500">Email Address</Label>
              <Input
                id="fp-email"
                type="email"
                required
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 border-slate-300 focus:border-[#00aaff] focus:ring-[#00aaff]"
                data-testid="fp-email-input"
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 bg-[#001f5c] hover:bg-[#001845] text-white font-semibold" data-testid="fp-submit">
              {loading ? 'Sending...' : 'Send reset link'}
            </Button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
